import React, { useState } from 'react';
import { Trophy, Medal, Award, Users, TrendingUp, Calendar, Crown, Zap, Star } from 'lucide-react';
import { standingsData, tournaments, TournamentStanding } from '../data/standingsData';

const TournamentStandings: React.FC = () => {
  const [showAll, setShowAll] = useState(false);
  
  // Display top 15 by default, or all if showAll is true
  const displayData = showAll ? standingsData : standingsData.slice(0, 15);
  
  // Get ranking icon based on position
  const getRankIcon = (rank: number) => {
    if (rank === 1) return <Crown className="w-6 h-6 text-yellow-400" />;
    if (rank === 2) return <Medal className="w-6 h-6 text-gray-300" />;
    if (rank === 3) return <Award className="w-6 h-6 text-orange-400" />;
    if (rank <= 5) return <Star className="w-5 h-5 text-blue-400" />;
    return <span className="w-6 h-6 flex items-center justify-center text-sm font-bold text-white/60 bg-white/10 rounded-full">#{rank}</span>;
  };
  
  // Format points display - show 0 as "-" for cleaner look
  const formatPoints = (points: number): string => {
    return points === 0 ? '-' : points.toString();
  };
  
  // Get row styling based on rank
  const getRowStyling = (rank: number): string => {
    if (rank === 1) return 'bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border-l-4 border-yellow-400 rank-glow-1';
    if (rank === 2) return 'bg-gradient-to-r from-gray-400/20 to-slate-400/20 border-l-4 border-gray-300';
    if (rank === 3) return 'bg-gradient-to-r from-orange-500/20 to-red-500/20 border-l-4 border-orange-400';
    if (rank <= 5) return 'bg-gradient-to-r from-blue-500/10 to-purple-500/10 border-l-4 border-blue-400';
    if (rank <= 10) return 'bg-gradient-to-r from-green-500/10 to-blue-500/10 border-l-4 border-green-400';
    return 'bg-white/5 hover:bg-white/10 border-l-4 border-transparent hover:border-white/20';
  };

  return (
    <div className="standings-table rounded-3xl overflow-hidden shadow-2xl">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-800 via-purple-900 to-slate-800 px-8 py-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-2xl">
              <TrendingUp className="w-8 h-8 text-white" />
            </div>
            <div>
              <h3 className="text-3xl font-black text-white">Championship Standings</h3>
              <p className="text-white/60 text-lg">2025 Angler of the Year Battle</p>
            </div>
          </div>
          <div className="flex items-center space-x-3 text-white/80">
            <Users className="w-5 h-5" />
            <span className="text-lg font-semibold">{standingsData.length} Elite Anglers</span>
          </div>
        </div>
      </div>

      {/* Tournament Status Bar */}
      <div className="bg-slate-800/50 px-8 py-4 border-b border-white/10">
        <div className="flex flex-wrap items-center justify-between">
          <div className="flex items-center space-x-6">
            <span className="text-lg font-bold text-white/90">Tournament Series:</span>
            <div className="flex space-x-3">
              {tournaments.map((tournament) => (
                <div
                  key={tournament.shortName}
                  className={`px-4 py-2 rounded-xl text-sm font-bold transition-all ${
                    tournament.completed
                      ? 'bg-gradient-to-r from-green-500 to-blue-500 text-white shadow-lg'
                      : 'bg-white/10 text-white/60 border border-white/20'
                  }`}
                >
                  <span className="flex items-center gap-2">
                    {tournament.shortName} 
                    {tournament.completed ? (
                      <Zap className="w-4 h-4" />
                    ) : (
                      <div className="w-2 h-2 bg-white/40 rounded-full" />
                    )}
                  </span>
                </div>
              ))}
            </div>
          </div>
          <div className="text-white/60 flex items-center space-x-2">
            <Calendar className="w-4 h-4" />
            <span className="font-medium">Live Updates • March 2025</span>
          </div>
        </div>
      </div>

      {/* Standings Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gradient-to-r from-slate-700 to-slate-800 border-b border-white/10">
            <tr>
              <th className="px-6 py-4 text-left text-sm font-black text-white/90 uppercase tracking-wider">
                Position
              </th>
              <th className="px-6 py-4 text-left text-sm font-black text-white/90 uppercase tracking-wider">
                Elite Angler
              </th>
              <th className="px-3 py-4 text-center text-sm font-black text-white/90 uppercase tracking-wider">
                D1
              </th>
              <th className="px-3 py-4 text-center text-sm font-black text-white/90 uppercase tracking-wider">
                SS
              </th>
              <th className="px-3 py-4 text-center text-sm font-black text-white/90 uppercase tracking-wider">
                D2
              </th>
              <th className="px-3 py-4 text-center text-sm font-black text-white/90 uppercase tracking-wider">
                TU
              </th>
              <th className="px-3 py-4 text-center text-sm font-black text-white/90 uppercase tracking-wider">
                TOC
              </th>
              <th className="px-6 py-4 text-center text-sm font-black text-white/90 uppercase tracking-wider">
                Best 2
              </th>
              <th className="px-6 py-4 text-center text-sm font-black text-white/90 uppercase tracking-wider">
                Total
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {displayData.map((angler, index) => (
              <tr
                key={`${angler.rank}-${angler.name}`}
                className={`transition-all duration-300 hover:scale-[1.02] ${getRowStyling(angler.rank)}`}
              >
                <td className="px-6 py-5 whitespace-nowrap">
                  <div className="flex items-center space-x-3">
                    {getRankIcon(angler.rank)}
                    <span className="text-lg font-black text-white">{angler.rank}</span>
                  </div>
                </td>
                <td className="px-6 py-5 whitespace-nowrap">
                  <div className={`text-lg font-bold ${angler.rank <= 3 ? 'text-white' : 'text-white/90'}`}>
                    {angler.name}
                    {angler.rank === 1 && <span className="ml-2 text-yellow-400">👑</span>}
                  </div>
                </td>
                <td className="px-3 py-5 whitespace-nowrap text-center">
                  <span className={`text-lg font-bold px-3 py-1 rounded-lg ${
                    angler.delta > 0 
                      ? 'text-white bg-white/10' 
                      : 'text-white/40'
                  }`}>
                    {formatPoints(angler.delta)}
                  </span>
                </td>
                <td className="px-3 py-5 whitespace-nowrap text-center">
                  <span className={`text-lg font-bold px-3 py-1 rounded-lg ${
                    angler.saltSprings > 0 
                      ? 'text-white bg-white/10' 
                      : 'text-white/40'
                  }`}>
                    {formatPoints(angler.saltSprings)}
                  </span>
                </td>
                <td className="px-3 py-5 whitespace-nowrap text-center">
                  <span className={`text-lg font-bold px-3 py-1 rounded-lg ${
                    angler.delta2 > 0 
                      ? 'text-white bg-white/10' 
                      : 'text-white/40'
                  }`}>
                    {formatPoints(angler.delta2)}
                  </span>
                </td>
                <td className="px-3 py-5 whitespace-nowrap text-center">
                  <span className={`text-lg font-bold px-3 py-1 rounded-lg ${
                    angler.tulloch > 0 
                      ? 'text-white bg-white/10' 
                      : 'text-white/40'
                  }`}>
                    {formatPoints(angler.tulloch)}
                  </span>
                </td>
                <td className="px-3 py-5 whitespace-nowrap text-center">
                  <span className={`text-lg font-bold px-3 py-1 rounded-lg ${
                    angler.toc > 0 
                      ? 'text-white bg-white/10' 
                      : 'text-white/40'
                  }`}>
                    {formatPoints(angler.toc)}
                  </span>
                </td>
                <td className="px-6 py-5 whitespace-nowrap text-center">
                  <span className="text-xl font-black text-blue-400 bg-blue-500/20 px-4 py-2 rounded-xl">
                    {angler.totalAfterDrop}
                  </span>
                </td>
                <td className="px-6 py-5 whitespace-nowrap text-center">
                  <span className="text-xl font-black text-white bg-gradient-to-r from-purple-600 to-blue-600 px-4 py-2 rounded-xl shadow-lg">
                    {angler.totalPoints}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Footer with Show More/Less Button */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-700 px-8 py-6 flex items-center justify-between">
        <div className="text-white/80 font-semibold">
          Showing <span className="text-blue-400">{displayData.length}</span> of <span className="text-white">{standingsData.length}</span> championship anglers
        </div>
        <button
          onClick={() => setShowAll(!showAll)}
          className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white px-6 py-3 rounded-xl font-bold transition-all duration-300 transform hover:scale-105 shadow-lg"
        >
          {showAll ? 'Show Elite Top 15' : 'View All Competitors'}
        </button>
      </div>

      {/* Legend */}
      <div className="bg-slate-900/80 px-8 py-4 border-t border-white/5">
        <div className="text-sm text-white/70">
          <div className="flex flex-wrap items-center space-x-6 mb-2">
            <span><strong className="text-white">D1:</strong> Delta Championship</span>
            <span><strong className="text-white">SS:</strong> Salt Springs Pro</span>
            <span><strong className="text-white">D2:</strong> Delta Series II</span>
            <span><strong className="text-white">TU:</strong> Tulloch Elite</span>
            <span><strong className="text-white">TOC:</strong> Tournament of Champions</span>
          </div>
          <div className="text-white/50">
            <span className="text-blue-400 font-semibold">Best 2</span> tournament scores determine Angler of the Year standings • Live scoring system
          </div>
        </div>
      </div>
    </div>
  );
};

export default TournamentStandings;